import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/auth/AuthContext";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { CommunitySocketProvider } from "@/realtime/CommunitySocketProvider";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import StudentDashboard from "./pages/StudentDashboard";
import MyCoursesPage from "./pages/MyCoursesPage";
import CourseDetailsPage from "./pages/CourseDetailsPage";
import LessonView from "./pages/LessonView";
import ExamPage from "./pages/ExamPage";
import MockTestPage from "./pages/MockTestPage";
import MockTestEditorPage from "./pages/MockTestEditorPage";
import CreateMockTestPage from "./pages/CreateMockTestPage";
import CommunityPage from "./pages/CommunityPage";
import { ExamHistoryPage } from "./pages/ExamHistoryPage";
import TeacherDashboard from "./pages/TeacherDashboard";
import TeacherCoursesPage from "./pages/TeacherCoursesPage";
import AdminDashboard from "./pages/AdminDashboard";
import SettingsPage from "./pages/SettingsPage";
import ProfileSettingsPage from "./pages/ProfileSettingsPage";
import PasswordChangePage from "./pages/PasswordChangePage";
import AssignRolesPage from "./pages/AssignRolesPage";
import CreateCoursePage from "./pages/CreateCoursePage";
import CreateExamPage from "./pages/CreateExamPage";
import CertificatesPage from "./pages/CertificatesPage";
import TeacherStudentsPage from "./pages/TeacherStudentsPage";
import AdminCoursesPage from "./pages/AdminCoursesPage";
import AdminCourseStructurePage from "./pages/AdminCourseStructurePage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminMockTestsPage from "./pages/AdminMockTestsPage";
import AdminExamConfigPage from "./pages/AdminExamConfigPage";
import AdminMockTestPurchasesPage from "./pages/AdminMockTestPurchasesPage";
import AdminMockTestBundlesPage from "./pages/AdminMockTestBundlesPage";
import AdminMockTestSubscriptionsPage from "./pages/AdminMockTestSubscriptionsPage";
import MockTestBundlesPage from "./pages/MockTestBundlesPage";
import StudentAnalyticsPage from "./pages/StudentAnalyticsPage";
import AdminCouponsPage from "./pages/AdminCouponsPage";
import AdminPaymentsPage from "./pages/AdminPaymentsPage";
import PrivacyPolicyPage from "./pages/legal/PrivacyPolicyPage";
import TermsPage from "./pages/legal/TermsPage";
import RefundPolicyPage from "./pages/legal/RefundPolicyPage";
import CookiePolicyPage from "./pages/legal/CookiePolicyPage";
import ContactPage from "./pages/ContactPage";
import CouponDetailPage from "./pages/CouponDetailPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <CommunitySocketProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />

              {/* Public legal / policy pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/refund-policy" element={<RefundPolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/contact" element={<ContactPage />} />

              {/* Student (any authenticated user can access student area) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <StudentDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/courses"
                element={
                  <ProtectedRoute>
                    <MyCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/course" element={<CourseDetailsPage />} />
              <Route path="/lesson" element={<LessonView />} />
              <Route path="/exam" element={<ExamPage />} />
              <Route
                path="/exam-history"
                element={
                  <ProtectedRoute>
                    <ExamHistoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/mock-tests/:mockTestId/edit"
                element={
                  <ProtectedRoute>
                    <MockTestEditorPage />
                  </ProtectedRoute>
                }
              />
              <Route path="/mock-tests" element={<MockTestPage />} />
              <Route path="/mock-test-bundles" element={<MockTestBundlesPage />} />
              <Route
                path="/community"
                element={
                  <ProtectedRoute>
                    <CommunityPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/certificates"
                element={
                  <ProtectedRoute>
                    <CertificatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile-settings"
                element={
                  <ProtectedRoute>
                    <ProfileSettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/password-change"
                element={
                  <ProtectedRoute>
                    <PasswordChangePage />
                  </ProtectedRoute>
                }
              />

              {/* Teacher */}
              <Route
                path="/teacher"
                element={
                  <ProtectedRoute requiredRole="teacher">
                    <TeacherDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/courses"
                element={
                  <ProtectedRoute requiredRole="teacher">
                    <TeacherCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/create-exam"
                element={
                  <ProtectedRoute requiredPermissions={["create_exam"]}>
                    <CreateExamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/create-exam"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CreateExamPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/students"
                element={
                  <ProtectedRoute requiredPermissions={["manage_students"]}>
                    <TeacherStudentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/students/:userId/analytics"
                element={
                  <ProtectedRoute requiredPermissions={["manage_students"]}>
                    <StudentAnalyticsPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/exam-config"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminExamConfigPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/courses"
                element={
                  <ProtectedRoute requiredPermissions={["edit_course"]}>
                    <AdminCoursesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/courses/:courseId/structure"
                element={
                  <ProtectedRoute requiredPermissions={["edit_course"]}>
                    <AdminCourseStructurePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/courses/:courseId/structure"
                element={
                  <ProtectedRoute requiredPermissions={["edit_course"]}>
                    <AdminCourseStructurePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/create-course"
                element={
                  <ProtectedRoute requiredPermissions={["create_course"]}>
                    <CreateCoursePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/students/:userId/analytics"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <StudentAnalyticsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/roles"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AssignRolesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/certificates"
                element={
                  <ProtectedRoute requiredPermissions={["manage_certificates"]}>
                    <CertificatesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/coupons"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminCouponsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/coupons/:id"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CouponDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/coupons"
                element={
                  <ProtectedRoute requiredRole="teacher">
                    <AdminCouponsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/teacher/coupons/:id"
                element={
                  <ProtectedRoute requiredRole="teacher">
                    <CouponDetailPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mock-tests/new"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <CreateMockTestPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mock-test-purchases"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminMockTestPurchasesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/payments"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminPaymentsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mock-test-bundles"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminMockTestBundlesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/mock-test-subscriptions"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminMockTestSubscriptionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute requiredPermissions={["view_analytics"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </CommunitySocketProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
